/**
 * UUIDv7: 48-bit big-endian millisecond timestamp, then random bits.
 *
 * Item ids are generated here rather than on the server so that a mutation queued
 * offline carries its final id from the moment it is created — which is what lets the
 * server treat a replay as a no-op instead of a second item.
 *
 * v7 rather than v4 because the values sort by creation time, so the primary key index
 * stays dense as the table grows instead of scattering writes across it.
 *
 * `crypto.randomUUID()` would give a v4; there is no built-in v7 yet, hence this.
 */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  const timestamp = Date.now();

  // 48-bit timestamp, most significant byte first.
  bytes[0] = (timestamp / 2 ** 40) & 0xff;
  bytes[1] = (timestamp / 2 ** 32) & 0xff;
  bytes[2] = (timestamp / 2 ** 24) & 0xff;
  bytes[3] = (timestamp / 2 ** 16) & 0xff;
  bytes[4] = (timestamp / 2 ** 8) & 0xff;
  bytes[5] = timestamp & 0xff;

  // Version 7 in the high nibble of byte 6.
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // RFC 9562 variant in the top two bits of byte 8.
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}
